import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Textarea } from '../common/Textarea';
import { Button } from '../common/Button';
import type { Comment, CreateCommentRequest, UpdateCommentRequest } from '../../types/comment';
import { useLanguage } from '../../i18n/useLanguage';

interface CommentEditorProps {
  comment?: Comment;
  onSubmit: (data: CreateCommentRequest | UpdateCommentRequest) => Promise<void>;
  onCancel?: () => void;
  isLoading?: boolean;
}

type CommentFormData = { content: string };

export const CommentEditor: React.FC<CommentEditorProps> = ({
  comment,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const { t, language } = useLanguage();

  const commentSchema = useMemo(
    () =>
      z.object({
        content: z
          .string()
          .min(1, t('comment.validationContentRequired'))
          .max(5000, t('comment.validationContentMax')),
      }),
    [language, t]
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CommentFormData>({
    resolver: zodResolver(commentSchema),
    defaultValues: {
      content: comment?.content || '',
    },
  });

  useEffect(() => {
    if (comment) {
      reset({
        content: comment.content,
      });
    } else {
      reset({ content: '' });
    }
  }, [comment, reset]);

  const handleFormSubmit = async (data: CommentFormData) => {
    await onSubmit(data);
    if (!comment) {
      reset({ content: '' });
    }
  };

  return (
    <form className="comment-editor" onSubmit={handleSubmit(handleFormSubmit)}>
      <Textarea
        label={t('comment.editorLabel')}
        {...register('content')}
        error={errors.content?.message}
        placeholder={t('comment.editorPlaceholder')}
        rows={4}
        required
      />

      <div className="comment-editor-actions">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
        )}
        <Button type="submit" variant="primary" isLoading={isLoading}>
          {comment ? t('comment.submitUpdate') : t('comment.submitNew')}
        </Button>
      </div>
    </form>
  );
};
